import { ref } from 'vue'

/**
 * Scelta del formato data/ora (Impostazioni account > Preferenze).
 *
 * Come lingua e fuso orario, è salvata sull'account (`User.date_format`) e
 * quindi la stessa su ogni dispositivo dopo il login — vedi
 * `applyAccountPreference()`, chiamata da `services/session.ts` dopo
 * `fetchUser()`. Il `ref` di questo modulo resta comunque la sorgente che
 * `utils/datetime.ts` legge, con `localStorage` come cache: così un ospite (o
 * un reload prima che `getUser` risponda) vede l'ultimo formato noto invece del
 * default, e la pagina resta leggibile offline.
 */
export type DateFormatPreference = 'EU' | 'US' | 'UK' | 'ISO'

export const DATE_FORMAT_PREFERENCES: readonly DateFormatPreference[] = ['EU', 'US', 'UK', 'ISO']

export const DEFAULT_DATE_FORMAT: DateFormatPreference = 'EU'

const STORAGE_KEY = 'travick.dateFormat'

function isDateFormatPreference(value: unknown): value is DateFormatPreference {
  return typeof value === 'string' && (DATE_FORMAT_PREFERENCES as readonly string[]).includes(value)
}

function storedDateFormat(): DateFormatPreference | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return isDateFormatPreference(raw) ? raw : null
  } catch {
    return null
  }
}

/**
 * Sorgente unica del formato attivo. Un `ref` a livello di modulo, non uno
 * store Pinia: `utils/datetime.ts` lo legge fuori da ogni componente, e
 * l'accesso a `.value` resta comunque tracciato da Vue ovunque avvenga
 * (computed, render), quindi ogni testo che passa da `formatDate`/
 * `formatDateTime` si aggiorna da solo al cambio di preferenza.
 */
export const dateFormat = ref<DateFormatPreference>(storedDateFormat() ?? DEFAULT_DATE_FORMAT)

/** Formato attivo, per chi non vuole importare il `ref`. */
export function currentDateFormat(): DateFormatPreference {
  return dateFormat.value
}

export function setDateFormat(format: DateFormatPreference): void {
  dateFormat.value = format
  try {
    window.localStorage.setItem(STORAGE_KEY, format)
  } catch {
    // Non fatale: la scelta non sopravvive al reload.
  }
}

/**
 * Riallinea il formato attivo a quello salvato sull'account, se ne ha uno.
 *
 * Un account senza preferenza (`null`, nessuna scelta fatta) non tocca nulla:
 * resta quello che localStorage/il default avevano già deciso per questo
 * browser. Chiamata da `services/session.ts` dopo ogni `fetchUser()` riuscito.
 */
export function applyAccountPreference(value: string | null): void {
  if (isDateFormatPreference(value)) setDateFormat(value)
}
